import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  TextField,
} from '@leap4/frontend';
import { MuiIcons } from '@leap4/frontend';
import { IReportConstraint } from '../report.interfaces';
import { CONSTRAINT_OPERATORS, REPORT_METRICS } from '../report.constants';

const { Delete, Add, ExpandMore, ChevronRight } = MuiIcons;

interface ConstraintsSectionProps {
  constraints: IReportConstraint[];
  selectedMetrics: string[];
  onAddConstraint: () => void;
  onRemoveConstraint: (id: number) => void;
  onUpdateConstraint: (id: number, key: keyof IReportConstraint, value: any) => void;
  onAddAllMetricConstraints: () => void;
}

export const ConstraintsSection: React.FC<ConstraintsSectionProps> = ({
  constraints,
  selectedMetrics,
  onAddConstraint,
  onRemoveConstraint,
  onUpdateConstraint,
  onAddAllMetricConstraints,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const availableMetrics = REPORT_METRICS.filter((m) => selectedMetrics.includes(m.key));

  return (
    <Box>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {isOpen ? (
          <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
        ) : (
          <ChevronRight sx={{ fontSize: 20, color: 'text.secondary' }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: 11,
          }}
        >
          Constraints
        </Typography>
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
          {constraints.map((constraint) => (
            <Box
              key={constraint.id}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={constraint.metric || ''}
                  onChange={(e) => onUpdateConstraint(constraint.id, 'metric', e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 1 }}
                  renderValue={(selected) => {
                    if (!selected) return <Typography color="text.secondary">Select metric</Typography>;
                    return availableMetrics.find((m) => m.key === selected)?.label || selected;
                  }}
                >
                  {availableMetrics.map((m) => (
                    <MenuItem key={m.key} value={m.key}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                value={constraint.operator}
                exclusive
                onChange={(_, value) => value && onUpdateConstraint(constraint.id, 'operator', value)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    borderRadius: 1,
                    px: 1.5,
                    minWidth: 36,
                    fontWeight: 600,
                    fontSize: 13,
                  },
                }}
              >
                {CONSTRAINT_OPERATORS.map((op) => (
                  <ToggleButton
                    key={op}
                    value={op}
                    sx={{
                      '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                    }}
                  >
                    {op}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <TextField
                size="small"
                type="number"
                placeholder="Value"
                value={constraint.value}
                onChange={(e) => onUpdateConstraint(constraint.id, 'value', e.target.value)}
                sx={{ 
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                }}
              />

              <IconButton
                size="small"
                onClick={() => onRemoveConstraint(constraint.id)}
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<Add />}
              onClick={onAddConstraint}
              sx={{ 
                borderStyle: 'dashed',
                borderRadius: 1,
                textTransform: 'none',
                px: 2,
              }}
            >
              Add Constraint
            </Button>
            {selectedMetrics.length > 0 && (
              <Button 
                variant="outlined" 
                size="small" 
                onClick={onAddAllMetricConstraints}
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'none',
                  px: 2,
                }}
              >
                Add All Selected Metrics
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
